import { useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getGetProjectsOptionsQueryKey,
  useCreateProjectTag,
  useDeleteProjectTag,
  useGetProjectsOptions,
  useUpdateProjectTag,
} from '@/entities/project/api/generated/api';
import { GetProjectsOptionsFieldsItem } from '@/entities/project/api/generated/model';
import { invalidateByPathPrefix } from '@/shared/lib/query/invalidateByPathPrefix';
import { type TagNameError, validateTagName } from '@/shared/lib/validation/validateTagName';
import { toast } from '@/shared/ui';
import {
  type TagOption,
  TagSelectWithCrud,
} from '@/shared/ui/tag-select-with-crud/TagSelectWithCrud';

const OPTIONS_PARAMS = { fields: [GetProjectsOptionsFieldsItem.tag] };

type Props = {
  selectedTagIds: string[];
  onSelectedTagIdsChange: (tagIds: string[]) => void;
};

/**
 * 事業フォーム内のタグ選択＋CRUD（API 連携）。
 */
export const ProjectFormTagSelect = ({ selectedTagIds, onSelectedTagIdsChange }: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // 非同期コールバックで最新の選択 ID にアクセスするための ref
  const latestSelectedRef = useRef(selectedTagIds);
  latestSelectedRef.current = selectedTagIds;

  const { data: optionsData } = useGetProjectsOptions(OPTIONS_PARAMS);

  const tags: TagOption[] = (optionsData?.tags ?? []).map((tag) => ({
    id: tag.id,
    name: tag.name,
  }));

  const validationMessage = (code: TagNameError) => {
    const map: Record<TagNameError, string> = {
      required: t('tag.validation.required'),
      maxLength: t('tag.validation.maxLength'),
      duplicate: t('tag.validation.duplicate'),
    };
    return map[code];
  };

  const invalidateOptions = () => {
    queryClient.invalidateQueries({ queryKey: getGetProjectsOptionsQueryKey(OPTIONS_PARAMS) });
  };

  const invalidateProjectData = () => {
    invalidateByPathPrefix(queryClient, {
      include: ['/projects'],
      exclude: ['/projects/options', '/projects/eucs'],
    });
  };

  const { mutate: createTag } = useCreateProjectTag({ request: { suppressToast: true } });
  const { mutateAsync: updateTagAsync } = useUpdateProjectTag({
    request: { suppressToast: true },
  });
  const { mutate: deleteTag } = useDeleteProjectTag({ request: { suppressToast: true } });

  const handleCreateTag = (name: string) => {
    const error = validateTagName(tags, name);
    if (error) {
      toast.error(validationMessage(error));
      return;
    }
    createTag(
      { data: { name: name.trim() } },
      {
        onSuccess: (response) => {
          toast.success(t('tag.messages.createSuccess'));
          onSelectedTagIdsChange([...latestSelectedRef.current, response.id]);
          invalidateOptions();
        },
        onError: () => {
          toast.error(t('common.messages.create-failed', { name: t('project.fields.tags') }));
        },
      },
    );
  };

  const handleUpdateTag = async (tagId: string, name: string): Promise<boolean> => {
    const error = validateTagName(tags, name, tagId);
    if (error) {
      toast.error(validationMessage(error));
      return false;
    }
    try {
      await updateTagAsync({ tagId, data: { name: name.trim() } });
      toast.success(t('tag.messages.updateSuccess'));
      invalidateOptions();
      invalidateProjectData();
      return true;
    } catch {
      toast.error(t('common.messages.update-failed', { name: t('project.fields.tags') }));
      return false;
    }
  };

  const handleDeleteTag = (tagId: string) => {
    deleteTag(
      { tagId },
      {
        onSuccess: () => {
          toast.success(t('tag.messages.deleteSuccess'));
          // フォーム state からも削除済みタグを除外
          onSelectedTagIdsChange(latestSelectedRef.current.filter((id) => id !== tagId));
          invalidateOptions();
          invalidateProjectData();
        },
        onError: () => {
          toast.error(t('common.messages.delete-failed', { name: t('project.fields.tags') }));
        },
      },
    );
  };

  return (
    <TagSelectWithCrud
      tags={tags}
      selectedTagIds={selectedTagIds}
      onSelectedTagIdsChange={onSelectedTagIdsChange}
      onCreateTag={handleCreateTag}
      onUpdateTag={handleUpdateTag}
      onDeleteTag={handleDeleteTag}
    />
  );
};
